import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * KPI strip — vízszintes tile-sor a fő oldalak tetején.
 * Nagy szám (`num-display` tabular), kis label, opcionális trend / sub-text.
 *
 * Hangulat: Stripe/Linear-szerű — visszafogott, élesen olvasható számok.
 * Mobil: vertikális stackelés, desktop: 2-4 oszlopos rács.
 */
export interface StatTile {
  label: string
  value: ReactNode
  /** Másodlagos, halványabb felirat a szám alatt (pl. "ebből 3 EUR-os"). */
  sub?: ReactNode
  /** Opcionális trend kis-tile: + 12% (zöld), -5% (piros). */
  trend?: { value: string; tone: 'up' | 'down' | 'neutral' }
  /** Tetszőleges ikon a left-felső sarokban (lucide-react). */
  icon?: ReactNode
  /** Tone-szín — accentált tile (pl. NAV sync OK = accent). */
  tone?: 'default' | 'brand' | 'accent' | 'warn'
  /** Élő státusz (`breathe` indikátor a label mellett). */
  live?: boolean
}

const TONE_CLASSES: Record<NonNullable<StatTile['tone']>, string> = {
  default:
    'border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/40',
  brand:
    'border-brand-200/80 dark:border-brand-900/40 bg-gradient-to-br from-brand-50/80 to-white dark:from-brand-950/40 dark:to-slate-900/40',
  accent:
    'border-accent-200/80 dark:border-accent-900/40 bg-gradient-to-br from-accent-50/80 to-white dark:from-accent-950/30 dark:to-slate-900/40',
  warn:
    'border-amber-200/80 dark:border-amber-900/40 bg-gradient-to-br from-amber-50/80 to-white dark:from-amber-950/30 dark:to-slate-900/40',
}

export function StatsStrip({ tiles, className }: { tiles: StatTile[]; className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-' + Math.min(4, tiles.length || 1),
        'gap-3 mb-4',
        className,
      )}
    >
      {tiles.map((t, i) => (
        <Tile key={i} t={t} />
      ))}
    </div>
  )
}

function Tile({ t }: { t: StatTile }) {
  const tone = t.tone ?? 'default'
  return (
    <div
      className={cn(
        'group relative rounded-xl2 border px-4 py-3.5 shadow-soft',
        'transition-all duration-200 ease-out-soft',
        'hover:shadow-card hover:-translate-y-px',
        TONE_CLASSES[tone],
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400">
          {t.icon && (
            <span
              className={cn(
                'inline-flex h-4 w-4 items-center justify-center',
                tone === 'brand' && 'text-brand-600 dark:text-brand-300',
                tone === 'accent' && 'text-accent-600 dark:text-accent-300',
                tone === 'warn' && 'text-amber-600 dark:text-amber-300',
                tone === 'default' && 'text-slate-400 dark:text-slate-500',
              )}
            >
              {t.icon}
            </span>
          )}
          <span>{t.label}</span>
          {t.live && <span className="live-dot ml-1" aria-label="élő" />}
        </div>
        {t.trend && (
          <span
            className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
              t.trend.tone === 'up' &&
                'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-200',
              t.trend.tone === 'down' && 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
              t.trend.tone === 'neutral' && 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            {t.trend.value}
          </span>
        )}
      </div>
      <div className="mt-1.5 text-display-sm num-display text-slate-900 dark:text-slate-50">
        {t.value}
      </div>
      {t.sub && (
        <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{t.sub}</div>
      )}
    </div>
  )
}
